import {useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";
import messages from "./messages";
import {Grid, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import moment from "moment";
import RefrezhButton from "components/RefrezhButton";
import {withRouter} from "react-router";
import Base from "components/Message/Base";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  poDetails: {
    paddingBottom: theme.spacing(2),
    "&.active": {
      borderBottom: "1px solid rgba(0,0,0,0.46)",
    },
  },
  content: {
    padding: theme.spacing(2, 0),
  },
  actionContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  marginSides: {
    margin: theme.spacing(0, 2),
  },
  popover: {
    pointerEvents: "none",
  },
  paper: {
    padding: theme.spacing(1),
    borderRadius: "15px",
    border: "1px solid black",
    maxWidth: "300px",
    lineHeight: 1,
  },
  messageBox: {
    borderLeft: "none",
    border: "1px solid rgba(0,0,0,0.24)",
    borderColor: "rgba(0,0,0,0.24)",
  },
  infoContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&.old": {
      color: "#e68058",
    },
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    justifyContent: "center",
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
}));

function TreatmentPlanned({
  activeMessage = {},
  onClose,
  history,
  showPatient,
  title,
  onRefittingRequest,
  reFittingPlannedTreatment,
  onResetRefittingRequest
}) {
  const classes = useStyles();



  useEffect(
    () => {
      if (reFittingPlannedTreatment && reFittingPlannedTreatment.id > 0) {
          history.push({
            pathname: `/treatment/edit/${activeMessage.patient.id}`,
            reFittingPlannedTreatment:reFittingPlannedTreatment,}
          );
        onResetRefittingRequest();
      }
    },
    [activeMessage.patient.id, history, reFittingPlannedTreatment],
  );

  const actionMessagePlanned = useMemo(
    ()=>(
        <FormattedMessage
          {...messages.has_been_planned}
          values={{ name: activeMessage.plannedTreatment.treatmentName }}
        />
    )
  )

  const actionMessageChanged = useMemo(
    ()=>(
      <FormattedMessage
        {...messages.planned_treatment_changed_thanks}
        values={{ name: activeMessage.plannedTreatment.treatmentName }}
      />
    )
  )

  const body = useMemo(
    () => (
      <div className={classes.body}>
        <div className={clsx(classes.poDetails, {
          active: true
        })}>
          <Typography>
            { activeMessage.type === 7 ? (actionMessagePlanned):(actionMessageChanged)}
          </Typography>
          <div className={clsx(classes.infoContainer, {
            old: activeMessage.latestMessageId !== activeMessage.id
          })}>
            <div className={classes.textContainer}>
              <Typography variant="caption">
                <FormattedMessage {...messages.planned_treatment_date} />
              </Typography>
              <Typography>
                {moment(
                  activeMessage.planned_date_time_from || activeMessage.plannedTreatment.planned_date_time_from,
                ).format("DD-MM-YYYY")}
              </Typography>
            </div>
            <div className={classes.textContainer}>
              <Typography variant="caption">
                <FormattedMessage {...messages.planned_treatment_time} />
              </Typography>
              <Typography>
                {moment(
                  activeMessage.planned_date_time_from || activeMessage.plannedTreatment.planned_date_time_from,
                ).format("HH.mmu")}{" "}
                -{" "}
                {moment(
                  activeMessage.planned_date_time_to || activeMessage.plannedTreatment.planned_date_time_to,
                ).format("HH.mmu")}
              </Typography>
            </div>
            {(activeMessage.bumbalDriver || activeMessage.plannedTreatment.bumbalDriver) && (
              <div className={classes.textContainer}>
                <Typography variant="caption">
                  <FormattedMessage {...messages.planned_treatment_kpt} />
                </Typography>
                <Typography>
                  {activeMessage.bumbalDriver?.first_name || activeMessage.plannedTreatment.bumbalDriver.first_name}
                </Typography>
              </div>
            )}
          </div>
          {activeMessage.treatmentPlan.description && (
            <div className={classes.infoContainer}>
              <div className={classes.textContainer}>
                <Typography variant="caption">
                  <FormattedMessage {...messages.planned_treatment_comment} />
                </Typography>
                <Typography>
                  {activeMessage.treatmentPlan.description}
                </Typography>
              </div>
            </div>
          )}
        </div>
        <Grid container className={clsx(classes.poDetails, {
          active: activeMessage.latestMessageId === activeMessage.id
        })}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption">
              <FormattedMessage {...messages.order_placed_upper_product} />
            </Typography>
            <Typography variant="subtitle1">
              {activeMessage.plannedTreatment.upperProductName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption">
              <FormattedMessage {...messages.order_placed_lower_product} />
            </Typography>
            <Typography variant="subtitle1">
              {activeMessage.plannedTreatment.lowerProductName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption">
              <FormattedMessage {...messages.order_placed_phase} />
            </Typography>
            <Typography variant="subtitle1">
              {activeMessage.plannedTreatment.treatmentName}
            </Typography>
          </Grid>
        </Grid>
        { activeMessage.latestMessageId === activeMessage.id &&(
        <div className={classes.actionContainer}>
          {activeMessage.plannedTreatment.editPossible && (
          <RefrezhButton
            onClick={() => {
              history.push(
                `/treatment/edit/${activeMessage.patient.id}/${
                  activeMessage.plannedTreatment.id
                }`,
                {
                  activeTreatment: activeMessage.plannedTreatment,
                },
              );
            }}
            className={classes.marginSides}
          >
            <FormattedMessage
              {...messages.planned_treatment_edit_appointment}
            />
          </RefrezhButton>)}
          {activeMessage.plannedTreatment.nextTreatmentAvailable && (
            <RefrezhButton
              onClick={() => {
                history.push(
                  `/treatment/edit/${activeMessage.patient.id}/${
                    activeMessage.plannedTreatment.id
                  }`,
                );
              }}
              className={classes.marginSides}
            >
              <FormattedMessage
                {...messages.planned_treatment_next_appointment}
              />
            </RefrezhButton>
          )}
          {activeMessage?.plannedTreatment?.refittingPossible && (

            <div data-cy="refittingButton">
              <RefrezhButton
                onClick={()=>{onRefittingRequest(
                  {
                    practiceId: activeMessage.practiceId,
                    plannedTreatmentId: activeMessage.plannedTreatment.id,
                  }
                )}}
                className={classes.marginSides}
              >
                <FormattedMessage {...messages.order_placed_reorder_action} />
              </RefrezhButton>
            </div>
          )}
        </div>)}
      </div>
    ),
    [
      classes.body,
      classes.poDetails,
      classes.actionContainer,
      classes.marginSides,
      classes.infoContainer,
      classes.textContainer,
      activeMessage.patient,
      activeMessage.plannedTreatment,
      activeMessage.treatmentPlan.description,
      history,
    ],
  );

  return (
    <Base
      onClose={onClose}
      activeMessage={activeMessage}
      title={title}
      shouldRenderPatient={showPatient}
    >
      {body}
    </Base>
  );
}

TreatmentPlanned.propTypes = {
  activeMessage: PropTypes.shape({
    updatedAt: PropTypes.number,
    plannedTreatment: PropTypes.shape({
      lowerProductName: PropTypes.string,
      upperProductName: PropTypes.string,
      treatmentName: PropTypes.string,
      avadentCaseId: PropTypes.string,
      deliveryDate: PropTypes.number,
      planned_date_time_from: PropTypes.string,
    }),
    treatmentPlan: PropTypes.shape({
      caseId: PropTypes.string,
    }),
    patient: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  onRefittingRequest: PropTypes.func,
  onResetRefittingRequest: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  reFittingPlannedTreatment: PropTypes.object,
};

export default withRouter(TreatmentPlanned);
